import { useEffect, useRef } from "react";
import type { Timestamp } from "firebase/firestore";

import { getAnnotationMetadata, getImageDownloadURL } from "@/utils/entryUtils";

export interface AnnotationSequencePreloadTarget {
  imageUrl: string;
  metadataPath?: string;
  uploadStatus?: string;
  updatedAt?: Timestamp;
}

interface UseAnnotationSequencePreloaderParams {
  targets: AnnotationSequencePreloadTarget[];
  enabled: boolean;
}

const getPreloadKey = (target: AnnotationSequencePreloadTarget): string =>
  [target.imageUrl, target.metadataPath ?? "", target.updatedAt?.seconds ?? ""].join("|");

const MAX_PRELOADED_KEYS = 100;

function rememberPreloadedKey(keys: Set<string>, key: string): void {
  keys.add(key);

  while (keys.size > MAX_PRELOADED_KEYS) {
    const oldestKey = keys.values().next().value;
    if (!oldestKey) break;
    keys.delete(oldestKey);
  }
}

export function useAnnotationSequencePreloader({ targets, enabled }: UseAnnotationSequencePreloaderParams): void {
  const preloadedKeysRef = useRef<Set<string>>(new Set());
  const inFlightKeysRef = useRef<Set<string>>(new Set());
  const generationRef = useRef(0);
  const targetSignature = targets.map(getPreloadKey).join("::");

  useEffect(() => {
    if (!enabled) {
      generationRef.current += 1;
      preloadedKeysRef.current.clear();
      inFlightKeysRef.current.clear();
      return;
    }

    const generation = generationRef.current;

    const uniqueTargets = new Map<string, AnnotationSequencePreloadTarget>();
    targets.forEach((target) => {
      if (!target.imageUrl) return;
      uniqueTargets.set(getPreloadKey(target), target);
    });

    uniqueTargets.forEach((target, key) => {
      if (preloadedKeysRef.current.has(key) || inFlightKeysRef.current.has(key)) return;
      inFlightKeysRef.current.add(key);

      void (async () => {
        try {
          const downloadUrl = await getImageDownloadURL(target.imageUrl, target.uploadStatus, target.updatedAt);
          if (generation !== generationRef.current) return;
          if (!downloadUrl) return;

          const image = new Image();
          image.src = downloadUrl;

          if (target.metadataPath) {
            const metadata = await getAnnotationMetadata(target.metadataPath, target.updatedAt);
            if (generation !== generationRef.current) return;
            if (!metadata) return;
          }

          rememberPreloadedKey(preloadedKeysRef.current, key);
        } catch {
          // Best-effort preload only.
        } finally {
          if (generation === generationRef.current) {
            inFlightKeysRef.current.delete(key);
          }
        }
      })();
    });
    // targetSignature is the semantic dependency; targets array identity is intentionally ignored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, targetSignature]);
}
